import {
    AfterViewInit,
    Component,
    ElementRef,
    inject,
    input,
    OnDestroy,
    output,
    signal,
    ViewChild,
} from '@angular/core';
import * as markerjs3 from '@markerjs/markerjs3';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { RaToastService } from '@shared/components';
import { RaTooltipDirective } from '../../directives';
import { RaButtonComponent } from '../ra-button';
import { RaIconComponent } from '../ra-icon/ra-icon.component';
import { RaLoadingComponent } from '../ra-loading/ra-loading.component';
import { EditedImageResult } from './model/edited-image-result.model';
import { ImageCacheManager } from './services/image-cache-manager.service';
import { ImageRendererService } from './services/image-renderer.service';
import { MarkerAreaManagerService } from './services/marker-area-manager.service';
import { MarkerOperationsService } from './services/marker-operations.service';

@Component({
    selector: 'ra-edit-image',
    imports: [RaTooltipDirective, RaButtonComponent, RaLoadingComponent, TranslatePipe, RaIconComponent],
    templateUrl: './ra-edit-image.component.html',
    styleUrl: './ra-edit-image.component.scss',
    providers: [
        ImageCacheManager,
        ImageRendererService,
        MarkerOperationsService,
        MarkerAreaManagerService,
    ],
})
export class RaEditImageComponent implements AfterViewInit, OnDestroy {
    /** URL única da imagem (modo compatibilidade) */
    public imageUrl = input<string>();
    /** Lista de URLs das imagens para edição em lote */
    public imageUrls = input<string[]>();
    public close = output<void>();
    /** Emite lista de imagens editadas com position, file e size */
    public imageSaved = output<EditedImageResult[]>();

    @ViewChild('imageElement') imageElement!: ElementRef<HTMLImageElement>;
    @ViewChild('markerContainer', { static: false }) markerContainer!: ElementRef<HTMLDivElement>;

    private markerArea!: markerjs3.MarkerArea;
    private keydownHandler?: (event: KeyboardEvent) => void;
    private initialMarkerState: any = null; // Armazena o estado inicial dos marcadores
    public hasSelectedMarker = signal<boolean>(false); // Indica se há marcador selecionado
    public selectedColor = signal<string>('#ff0000'); // Cor selecionada (vermelho padrão)
    public isSaving = signal<boolean>(false); // Indica se está salvando a imagem
    public isLoadingImage = signal<boolean>(false); // Indica se está carregando a imagem

    // Controle de múltiplas imagens
    public currentImageIndex = signal<number>(0);
    public imagesList = signal<string[]>([]); // Lista de URLs das imagens
    private hasEmittedSaved = false; // Flag para evitar duplicação de eventos

    // * [Injects]
    private _raToastService = inject(RaToastService);
    private _translate = inject(TranslateService);
    private _imageCacheManager = inject(ImageCacheManager);
    private _imageRenderer = inject(ImageRendererService);
    private _markerOperations = inject(MarkerOperationsService);
    private _markerAreaManager = inject(MarkerAreaManagerService);

    ngAfterViewInit(): void {
        // Usa setTimeout para evitar ExpressionChangedAfterItHasBeenCheckedError
        setTimeout(() => {
            // Determina a lista de imagens (suporta modo único ou múltiplo)
            const singleImageUrl = this.imageUrl();
            const multipleImageUrls = this.imageUrls();

            let images: string[] = [];

            if (multipleImageUrls && multipleImageUrls.length > 0) {
                images = multipleImageUrls;
            } else if (singleImageUrl) {
                images = [singleImageUrl];
            } else {
                return;
            }

            this.imagesList.set(images);

            // Inicializa a primeira imagem
            this.loadImage(0).catch(() => {});
        }, 0);
    }

    /**
     * Carrega uma imagem específica pelo índice
     */
    private async loadImage(index: number): Promise<void> {
        const images = this.imagesList();
        if (index < 0 || index >= images.length || this.isLoadingImage()) {
            return;
        }

        this.isLoadingImage.set(true);
        const img = this.imageElement.nativeElement;
        const targetIndex = index; // Guarda o índice alvo

        // Salva automaticamente a imagem atual no cache antes de trocar (se houver alterações)
        const previousIndex = this.currentImageIndex();
        if (
            this.markerArea &&
            previousIndex >= 0 &&
            previousIndex !== targetIndex &&
            typeof this.markerArea.getState === 'function'
        ) {
            try {
                // Verifica se há alterações na imagem atual
                const currentState = this.markerArea.getState();
                const hasChanges = this._imageRenderer.hasMarkerChanges(
                    this.initialMarkerState,
                    currentState
                );

                if (hasChanges) {
                    // IMPORTANTE: Salva a imagem ANTES de navegar, usando a imagem atual do DOM
                    // Guarda a referência da imagem atual antes de mudar
                    const currentImage = this.imageElement.nativeElement;
                    const currentImageUrl = this.imagesList()[previousIndex];

                    // AGUARDA o salvamento antes de continuar a navegação
                    // Isso garante que a imagem correta seja salva antes do DOM mudar
                    try {
                        await this.saveCurrentImageToCacheSync(
                            previousIndex,
                            currentState,
                            currentImage,
                            currentImageUrl
                        );
                    } catch (error) {
                        // Ignora erros silenciosamente
                    }
                }

                // Força uma atualização final do cache antes de trocar
                this.updateMarkerStateCache();

                // Também salva diretamente para garantir
                if (currentState) {
                    // Salva o estado atual no cache
                    this._imageCacheManager.markerStatesCache.set(previousIndex, currentState);
                    // Se ainda não tem estado inicial salvo, salva também
                    if (!this._imageCacheManager.initialMarkerStatesCache.has(previousIndex)) {
                        this._imageCacheManager.initialMarkerStatesCache.set(
                            previousIndex,
                            this.initialMarkerState
                        );
                    }
                }
            } catch (error) {}
        }

        // Limpa intervalos de atualização anteriores
        this._imageCacheManager.clearAllIntervals();

        // Limpa o MarkerArea anterior completamente antes de carregar nova imagem
        if (this.markerArea) {
            try {
                // Remove do DOM diretamente - o MarkerArea não precisa ser fechado
                if (this.markerArea.parentElement) {
                    this.markerArea.parentElement.removeChild(this.markerArea);
                }
                // Limpa referências
                this.markerArea = null as any;
            } catch (error) {
                this.markerArea = null as any;
            }
        }

        // Reseta o estado inicial (será restaurado do cache se existir)
        this.initialMarkerState = null;

        // Remove event listeners antigos
        img.onload = null;
        img.onerror = null;

        // Atualiza o índice atual
        this.currentImageIndex.set(targetIndex);

        // Limpa o src anterior e esconde temporariamente
        img.src = '';
        img.style.opacity = '0';
        img.style.visibility = 'hidden';

        // Aguarda um pequeno delay para garantir que a limpeza foi processada
        requestAnimationFrame(() => {
            // Verifica se ainda estamos carregando a mesma imagem (evita race conditions)
            if (this.currentImageIndex() !== targetIndex) {
                this.isLoadingImage.set(false);
                return;
            }

            // Configura crossOrigin
            img.crossOrigin = 'anonymous';

            // Carrega a nova imagem
            const imageUrl = images[targetIndex];
            img.src = imageUrl;

            // Configura os handlers de load/error
            const onLoadHandler = () => {
                // Verifica novamente se ainda é a imagem correta
                if (this.currentImageIndex() === targetIndex && img.src === imageUrl) {
                    img.onload = null;
                    img.onerror = null;
                    img.style.opacity = '1';
                    img.style.visibility = 'visible';
                    this.initMarker(img);
                }
                this.isLoadingImage.set(false);
            };

            const onErrorHandler = () => {
                if (this.currentImageIndex() === targetIndex) {
                    img.onload = null;
                    img.onerror = null;
                    img.style.opacity = '1';
                    img.style.visibility = 'visible';
                }
                this.isLoadingImage.set(false);
            };

            if (img.complete && img.naturalWidth > 0 && img.src === imageUrl) {
                // Imagem já está carregada e é a correta
                onLoadHandler();
            } else {
                // Aguarda o carregamento
                img.onload = onLoadHandler;
                img.onerror = onErrorHandler;
            }
        });
    }

    ngOnDestroy(): void {
        // Remove o listener de teclado
        if (this.keydownHandler) {
            document.removeEventListener('keydown', this.keydownHandler);
            this.keydownHandler = undefined;
        }

        // Limpa recursos do MarkerArea quando o componente é destruído
        if (this.markerArea) {
            try {
                // Remove o MarkerArea do DOM
                if (this.markerArea.parentElement) {
                    this.markerArea.parentElement.removeChild(this.markerArea);
                }
            } catch (error) {}
        }
    }

    private setupSelectionListeners(): void {
        // Listener para quando um marcador é selecionado
        (this.markerArea as any).addEventListener('markerselect', () => {
            setTimeout(() => this.checkSelectedMarkers(), 50);
        });

        // Listener para quando um marcador é deselecionado
        (this.markerArea as any).addEventListener('markerdeselect', () => {
            setTimeout(() => this.checkSelectedMarkers(), 50);
        });

        // Listener para quando a área é clicada (deseleciona)
        (this.markerArea as any).addEventListener('areaclick', () => {
            setTimeout(() => this.checkSelectedMarkers(), 100);
        });

        // Listener para quando um marcador é criado
        (this.markerArea as any).addEventListener('markercreate', () => {
            setTimeout(() => this.checkSelectedMarkers(), 100);
        });

        // Verifica periodicamente se há marcadores selecionados (fallback)
        const checkInterval = setInterval(() => {
            if (this.markerArea) {
                this.checkSelectedMarkers();
            } else {
                clearInterval(checkInterval);
            }
        }, 300);
    }

    private checkSelectedMarkers(): void {
        if (!this.markerArea) {
            this.hasSelectedMarker.set(false);
            return;
        }

        try {
            // Usa o método do serviço que valida se os marcadores estão realmente selecionados
            const selectedMarkers = this._markerOperations.getSelectedMarkers(this.markerArea);
            const hasSelection = selectedMarkers.length > 0;

            this.hasSelectedMarker.set(hasSelection);
        } catch (error) {
            // Em caso de erro, assume que não há seleção
            this.hasSelectedMarker.set(false);
        }
    }

    public deleteSelectedMarker(): void {
        if (!this.markerArea) return;

        try {
            // Usa o serviço para deletar os marcadores selecionados
            this._markerOperations.deleteSelectedMarkers(this.markerArea);

            // Atualiza o estado imediatamente após remover
            this.hasSelectedMarker.set(false);

            // Verifica novamente após um pequeno delay para garantir
            setTimeout(() => {
                this.checkSelectedMarkers();
            }, 100);
        } catch (error) {
            // Em caso de erro, também atualiza o estado
            this.hasSelectedMarker.set(false);
        }
    }

    private setupKeyboardListener(): void {
        // Cria o handler de teclado
        this.keydownHandler = (event: KeyboardEvent) => {
            // Verifica se Cmd (Mac) ou Ctrl (Windows/Linux) está pressionado
            const isModifierPressed = event.metaKey || event.ctrlKey;

            // Verifica se é Command/Ctrl + Backspace ou Delete para deletar elementos
            // Isso evita conflitos com a edição de texto
            const isDeleteWithModifier =
                isModifierPressed &&
                (event.key === 'Delete' ||
                    event.key === 'Backspace' ||
                    event.code === 'Delete' ||
                    event.code === 'Backspace');

            // Verifica se é Cmd+C (copiar)
            const isCopy = (event.key === 'c' || event.code === 'KeyC') && isModifierPressed;

            // Verifica se é Cmd+V (colar)
            const isPaste = (event.key === 'v' || event.code === 'KeyV') && isModifierPressed;

            if (isCopy) {
                this.handleCopy(event);
            } else if (isPaste) {
                this.handlePaste(event);
            } else if (isDeleteWithModifier) {
                // Previne o comportamento padrão
                event.preventDefault();
                event.stopPropagation();

                if (!this.markerArea) return;

                // Usa o serviço para deletar os marcadores selecionados
                this._markerOperations.deleteSelectedMarkers(this.markerArea);

                // Atualiza o estado após remover
                setTimeout(() => {
                    this.checkSelectedMarkers();
                }, 100);
            }
        };

        // Adiciona o listener ao documento com capture para garantir que seja executado antes
        // Isso é importante para interceptar o evento antes que o MarkerArea o processe
        document.addEventListener('keydown', this.keydownHandler, true);
    }

    private handleCopy(event: KeyboardEvent): void {
        event.preventDefault();
        event.stopPropagation();

        if (!this.markerArea) return;
        this._markerOperations.copySelectedMarkers(this.markerArea);
    }

    private handlePaste(event: KeyboardEvent): void {
        event.preventDefault();
        event.stopPropagation();

        if (!this.markerArea) return;
        this._markerOperations.pasteMarkers(this.markerArea);
    }

    private initMarker(image: HTMLImageElement): void {
        // Aguarda um pequeno delay para garantir que o DOM está pronto
        setTimeout(() => {
            try {
                // Cria o MarkerArea usando o elemento customizado
                // O markerjs3 registra o elemento como 'mjs-marker-area'
                this.markerArea = document.createElement('mjs-marker-area') as markerjs3.MarkerArea;

                // Define a imagem alvo ANTES de adicionar ao DOM
                (this.markerArea as any).targetImage = image;

                // Aguarda a imagem estar totalmente renderizada para pegar as dimensões corretas
                const updateDimensions = () => {
                    // Dimensões visuais da imagem (após aplicar CSS com object-fit: contain)
                    // Essas são as dimensões que a imagem realmente ocupa na tela
                    const visualWidth = image.clientWidth || image.offsetWidth || image.width;
                    const visualHeight = image.clientHeight || image.offsetHeight || image.height;

                    // Dimensões NATURAIS da imagem (tamanho real do arquivo)
                    const naturalWidth = image.naturalWidth || image.width;
                    const naturalHeight = image.naturalHeight || image.height;

                    if (
                        naturalWidth > 0 &&
                        naturalHeight > 0 &&
                        visualWidth > 0 &&
                        visualHeight > 0
                    ) {
                        // IMPORTANTE: Usa as dimensões VISUAIS para o tamanho do MarkerArea
                        // Isso garante que o MarkerArea tenha o mesmo tamanho que a imagem exibida
                        // Mas mantém as dimensões naturais para renderização em alta qualidade
                        (this.markerArea as any).targetWidth = visualWidth;
                        (this.markerArea as any).targetHeight = visualHeight;

                        // Armazena as dimensões naturais para uso na renderização
                        (this.markerArea as any)._naturalWidth = naturalWidth;
                        (this.markerArea as any)._naturalHeight = naturalHeight;
                    }
                };

                // Aguarda a imagem estar totalmente renderizada antes de atualizar dimensões
                // Isso garante que as dimensões visuais estejam corretas após o CSS ser aplicado
                const waitForImageRender = () => {
                    const visualWidth = image.clientWidth || image.offsetWidth || 0;
                    const visualHeight = image.clientHeight || image.offsetHeight || 0;

                    if (visualWidth > 0 && visualHeight > 0) {
                        updateDimensions();
                    } else {
                        // Se as dimensões visuais ainda não estão disponíveis, aguarda mais um frame
                        requestAnimationFrame(waitForImageRender);
                    }
                };

                // Tenta atualizar imediatamente
                updateDimensions();

                // Se as dimensões ainda não estão disponíveis, aguarda até estarem
                if (
                    !(this.markerArea as any).targetWidth ||
                    !(this.markerArea as any).targetHeight ||
                    (this.markerArea as any).targetWidth <= 0 ||
                    (this.markerArea as any).targetHeight <= 0
                ) {
                    waitForImageRender();
                }

                // Adiciona o MarkerArea ao container
                const container = this.imageElement.nativeElement.parentElement;
                if (container) {
                    // Remove qualquer MarkerArea antigo que possa estar no container
                    const oldMarkerAreas = container.querySelectorAll('mjs-marker-area');
                    oldMarkerAreas.forEach(oldArea => {
                        try {
                            oldArea.remove();
                        } catch (e) {
                            // Ignora erros ao remover elementos antigos
                        }
                    });

                    container.appendChild(this.markerArea);

                    // Aguarda o custom element estar totalmente definido e inicializado
                    // O markerjs3 não tem método show() - o MarkerArea é exibido automaticamente quando está no DOM
                    const waitForMarkerAreaReady = async (): Promise<void> => {
                        try {
                            // Aguarda o custom element estar definido
                            await customElements.whenDefined('mjs-marker-area');

                            // Aguarda frames adicionais para garantir que está totalmente inicializado
                            await new Promise(resolve => requestAnimationFrame(resolve));
                            await new Promise(resolve => requestAnimationFrame(resolve));

                            // Verifica se o MarkerArea está pronto (conectado ao DOM)
                            // O markerjs3 não tem método show() - ele é exibido automaticamente
                            if (this.markerArea && this.markerArea.isConnected) {
                                // Aguarda um pouco para garantir que está totalmente inicializado
                                setTimeout(() => {
                                    this.continueRestoration();
                                }, 300);
                            } else {
                                // Tenta novamente após um delay
                                setTimeout(() => {
                                    if (this.markerArea && this.markerArea.isConnected) {
                                        setTimeout(() => {
                                            this.continueRestoration();
                                        }, 300);
                                    } else {
                                        // Tenta continuar mesmo assim
                                        setTimeout(() => {
                                            this.continueRestoration();
                                        }, 500);
                                    }
                                }, 500);
                            }
                        } catch (e) {
                            // Fallback: tenta continuar mesmo assim após um delay
                            setTimeout(() => {
                                this.continueRestoration();
                            }, 500);
                        }
                    };

                    waitForMarkerAreaReady();
                } else {
                    return;
                }

                // Configura o evento de criação de marcadores
                this.markerArea.addEventListener('markercreate', (event: any) => {
                    // Aplica a cor selecionada ao marcador recém-criado
                    if (event.detail && event.detail.markerEditor) {
                        setTimeout(() => {
                            this.applyColorToMarkerEditor(event.detail.markerEditor);
                        }, 50);
                    }
                    // Aguarda um pouco mais para garantir que o marcador está totalmente criado
                    setTimeout(() => {
                        this.updateMarkerStateCache();
                    }, 300);
                });

                // Limpa intervalos anteriores para esta imagem
                const currentIndexForInterval = this.currentImageIndex();
                const existingInterval =
                    this._imageCacheManager.cacheUpdateIntervals.get(currentIndexForInterval);
                if (existingInterval) {
                    clearInterval(existingInterval);
                }

                // Atualiza o cache periodicamente quando há mudanças (fallback)
                // Isso garante que o estado seja salvo mesmo se eventos específicos não forem disparados
                // Mas só inicia após um delay para não interferir na restauração
                setTimeout(() => {
                    const interval = setInterval(() => {
                        if (this.markerArea && !this._imageCacheManager.getIsRestoringState()) {
                            this.updateMarkerStateCache();
                        } else if (!this.markerArea) {
                            clearInterval(interval);
                            this._imageCacheManager.cacheUpdateIntervals.delete(
                                currentIndexForInterval
                            );
                        }
                    }, 2000); // Atualiza a cada 2 segundos (menos frequente para não interferir)
                    this._imageCacheManager.cacheUpdateIntervals.set(
                        currentIndexForInterval,
                        interval
                    );
                }, 2000); // Aguarda 2s antes de iniciar o intervalo (dá tempo para restauração)

                // Configura eventos para detectar seleção/deseleção de marcadores
                this.setupSelectionListeners();

                // Configura o listener de teclado para deletar marcadores selecionados
                this.setupKeyboardListener();

                // O estado inicial agora é obtido/restaurado no setTimeout acima (após show())
                // Não precisamos mais deste setTimeout separado

                // Configura o evento de renderização (quando a imagem é salva)
                (this.markerArea as any).addEventListener('render', async (event: any) => {
                    this.isSaving.set(true);
                    try {
                        // Renderiza usando o serviço
                        const currentIndex = this.currentImageIndex();
                        const currentUrl = this.imagesList()[currentIndex];
                        const image = this.imageElement.nativeElement;
                        const state = this.markerArea.getState();
                        const file = await this._imageRenderer.renderImage(
                            image,
                            state,
                            currentUrl
                        );

                        // Adiciona à lista de imagens editadas
                        this._imageCacheManager.addEditedImage(currentIndex, file);

                        // Atualiza o estado inicial no cache para permitir novas edições nesta imagem
                        const savedState = this.markerArea.getState();
                        this.initialMarkerState = savedState;
                        this._imageCacheManager.initialMarkerStatesCache.set(
                            currentIndex,
                            savedState
                        );
                        // Atualiza também o estado atual no cache
                        this._imageCacheManager.markerStatesCache.set(currentIndex, savedState);

                        this.isSaving.set(false);

                        // Verifica se é a última imagem
                        const totalImages = this.imagesList().length;
                        const isLastImage =
                            totalImages === 1 || this.currentImageIndex() === totalImages - 1;

                        if (isLastImage) {
                            // Se é a última imagem, emite o evento com todas as imagens editadas acumuladas
                            this.hasEmittedSaved = true;
                            const imagesToEmit = this._imageCacheManager.editedImages.map(img => ({
                                position: img.position,
                                file: img.file,
                                size: img.size,
                            }));
                            this.imageSaved.emit([...imagesToEmit]);
                            this.closeModal();
                        }
                        // Se não é a última imagem, não faz nada (a imagem já foi adicionada ao cache)
                    } catch (error) {
                        this.isSaving.set(false);
                    }
                });
            } catch (error) {}
        }, 200);
    }

    public closeModal(): void {
        // Não permite fechar o modal enquanto está salvando
        if (this.isSaving()) return;

        // Emite as imagens editadas antes de fechar (se houver alguma e ainda não foi emitido)
        // Isso garante que imagens intermediárias salvas sejam incluídas no evento
        if (this._imageCacheManager.editedImages.length > 0 && !this.hasEmittedSaved) {
            this.hasEmittedSaved = true;
            this.imageSaved.emit([...this._imageCacheManager.editedImages]);
        }

        // Reseta o estado de loading
        this.isSaving.set(false);

        // Limpa intervalos de atualização
        this._imageCacheManager.cacheUpdateIntervals.forEach(interval => clearInterval(interval));
        this._imageCacheManager.cacheUpdateIntervals.clear();

        // Limpa a lista de imagens editadas e reseta a flag
        this._imageCacheManager.editedImages = [];
        this.hasEmittedSaved = false;

        // Limpa o cache de estados dos marcadores
        this._imageCacheManager.markerStatesCache.clear();
        this._imageCacheManager.initialMarkerStatesCache.clear();

        // Reseta flag de restauração
        this._imageCacheManager.setIsRestoringState(false);

        // O MarkerArea será removido automaticamente quando o componente for destruído
        this.close.emit();
    }

    /**
     * Continua o processo de restauração após o MarkerArea estar pronto
     */
    private continueRestoration(): void {
        try {
            // Marca que estamos restaurando para evitar atualizações do cache
            this._imageCacheManager.setIsRestoringState(true);

            // Restaura o estado salvo se existir para esta imagem
            const currentIndex = this.currentImageIndex();
            const savedState = this._imageCacheManager.markerStatesCache.get(currentIndex);
            const savedInitialState =
                this._imageCacheManager.initialMarkerStatesCache.get(currentIndex);

            if (savedState && this.markerArea) {
                // Restaura o estado dos marcadores
                try {
                    // Restaura o estado diretamente - o MarkerArea não precisa ser fechado/aberto
                    // Ele funciona automaticamente quando está no DOM
                    setTimeout(() => {
                        if (this.markerArea) {
                            try {
                                this.markerArea.restoreState(savedState);
                            } catch (restoreErr) {}
                        }
                    }, 200);

                    // Verifica múltiplas vezes se a restauração funcionou
                    const checkRestoration = (attempt: number = 1) => {
                        setTimeout(
                            () => {
                                if (!this.markerArea) return;

                                const restoredState = this.markerArea.getState();
                                const restoredCount = restoredState.markers?.length || 0;
                                const savedCount = savedState.markers?.length || 0;

                                // Se não restaurou corretamente e ainda não tentou 3 vezes, tenta novamente
                                if (restoredCount !== savedCount && attempt < 3) {
                                    this.markerArea.restoreState(savedState);
                                    checkRestoration(attempt + 1);
                                }
                            },
                            attempt === 1 ? 500 : 800
                        );
                    };

                    // Aguarda mais tempo antes de começar a verificar
                    setTimeout(() => {
                        checkRestoration();
                    }, 600);

                    // Restaura também o estado inicial se existir
                    if (savedInitialState) {
                        this.initialMarkerState = savedInitialState;
                    } else {
                        // Se não tem estado inicial salvo, usa o estado atual como inicial
                        this.initialMarkerState = savedState;
                    }

                    // Aguarda mais tempo antes de permitir atualizações do cache
                    setTimeout(() => {
                        this._imageCacheManager.setIsRestoringState(false);
                    }, 1500);
                } catch (restoreError) {
                    // Se falhar, obtém o estado inicial normalmente
                    if (this.markerArea) {
                        this.initialMarkerState = this.markerArea.getState();
                    }
                    this._imageCacheManager.setIsRestoringState(false);
                }
            } else {
                // Se não há estado salvo, obtém o estado inicial normalmente
                if (this.markerArea) {
                    this.initialMarkerState = this.markerArea.getState();
                    // Salva o estado inicial no cache
                    this._imageCacheManager.initialMarkerStatesCache.set(
                        currentIndex,
                        this.initialMarkerState
                    );
                }
                this._imageCacheManager.setIsRestoringState(false);
            }
        } catch (e) {
            this._imageCacheManager.setIsRestoringState(false);
        }
    }

    /**
     * Atualiza o cache do estado dos marcadores da imagem atual
     */
    private updateMarkerStateCache(): void {
        // Não atualiza durante a restauração
        if (this._imageCacheManager.getIsRestoringState()) {
            return;
        }

        if (this.markerArea && typeof this.markerArea.getState === 'function') {
            try {
                const currentIndex = this.currentImageIndex();
                const currentState = this.markerArea.getState();

                // Só atualiza se o estado tiver marcadores ou se não houver estado salvo ainda
                // Isso evita sobrescrever um estado com marcadores por um estado vazio
                if (currentState) {
                    const hasMarkers = currentState.markers && currentState.markers.length > 0;
                    const existingState =
                        this._imageCacheManager.markerStatesCache.get(currentIndex);
                    const existingHasMarkers =
                        existingState?.markers && existingState.markers.length > 0;

                    // Atualiza se:
                    // 1. Tem marcadores no estado atual, OU
                    // 2. Não tem estado salvo ainda, OU
                    // 3. O estado salvo também não tem marcadores (ambos vazios)
                    if (hasMarkers || !existingState || !existingHasMarkers) {
                        this._imageCacheManager.markerStatesCache.set(currentIndex, currentState);
                    }
                }
            } catch (error) {}
        }
    }

    /**
     * Salva automaticamente a imagem atual no cache ao navegar entre imagens
     * Versão síncrona que usa a imagem passada como parâmetro para evitar problemas de timing
     */
    private async saveCurrentImageToCacheSync(
        position: number,
        state: any,
        image: HTMLImageElement,
        imageUrl: string
    ): Promise<void> {
        try {
            // Renderiza usando o serviço (que já cria uma nova imagem internamente)
            const file = await this._imageRenderer.renderImage(image, state, imageUrl);

            // Adiciona à lista de imagens editadas (sem mostrar toast)
            this._imageCacheManager.addEditedImage(position, file);

            // Atualiza o estado inicial no cache
            this._imageCacheManager.initialMarkerStatesCache.set(position, state);
            this._imageCacheManager.markerStatesCache.set(position, state);
        } catch (error) {}
    }

    /**
     * Navega para a imagem anterior
     */
    public previousImage(): void {
        if (this.currentImageIndex() > 0 && !this.isSaving()) {
            this.loadImage(this.currentImageIndex() - 1).catch(() => {});
        }
    }

    /**
     * Navega para a próxima imagem
     */
    public nextImage(): void {
        const images = this.imagesList();
        if (this.currentImageIndex() < images.length - 1 && !this.isSaving()) {
            this.loadImage(this.currentImageIndex() + 1).catch(() => {});
        }
    }

    /**
     * Verifica se há imagem anterior
     */
    public hasPreviousImage(): boolean {
        return this.currentImageIndex() > 0;
    }

    /**
     * Verifica se há próxima imagem
     */
    public hasNextImage(): boolean {
        const images = this.imagesList();
        return this.currentImageIndex() < images.length - 1;
    }

    /**
     * Retorna o total de imagens
     */
    public getTotalImages(): number {
        return this.imagesList().length;
    }

    public async save(): Promise<void> {
        if (!this.markerArea || this.isSaving()) return;

        try {
            // Obtém o estado atual dos marcadores
            const currentState = this.markerArea.getState();

            // Compara o estado atual com o estado inicial
            const hasChanges = this._imageRenderer.hasMarkerChanges(
                this.initialMarkerState,
                currentState
            );

            // Se não houver alterações, mostra toast de warning
            if (!hasChanges) {
                this._raToastService.warning({
                    message: this._translate.instant('shared.editImage.noChanges'),
                });
                this.isSaving.set(false);
                return;
            }

            this.isSaving.set(true);

            // Renderiza a imagem com todas as anotações usando o Renderer
            const renderer = new markerjs3.Renderer();
            const image = this.imageElement.nativeElement;

            // Garante que o renderer use a imagem no tamanho natural para máxima qualidade
            (renderer as any).targetImage = image;

            // Define explicitamente as dimensões naturais para renderização em alta qualidade
            if (image.naturalWidth && image.naturalHeight) {
                (renderer as any).targetWidth = image.naturalWidth;
                (renderer as any).targetHeight = image.naturalHeight;
            }

            // Renderiza usando o serviço
            const currentUrl = this.imagesList()[this.currentImageIndex()];
            const file = await this._imageRenderer.renderImage(image, currentState, currentUrl);

            // Adiciona à lista de imagens editadas
            const currentIndex = this.currentImageIndex();
            this._imageCacheManager.addEditedImage(currentIndex, file);

            // Atualiza o estado inicial no cache para permitir novas edições nesta imagem
            this.initialMarkerState = currentState;
            this._imageCacheManager.initialMarkerStatesCache.set(currentIndex, currentState);
            // Atualiza também o estado atual no cache
            this._imageCacheManager.markerStatesCache.set(currentIndex, currentState);

            this.isSaving.set(false);

            // SEMPRE emite o evento com todas as imagens editadas quando clicar em "Salvar"
            // Remove duplicatas mantendo apenas a última versão de cada posição
            const uniqueImages = new Map<number, EditedImageResult>();
            this._imageCacheManager.editedImages.forEach(img => {
                uniqueImages.set(img.position, img);
            });

            const imagesToEmit = Array.from(uniqueImages.values()).map(img => ({
                position: img.position,
                file: img.file,
                size: img.size,
            }));

            this.hasEmittedSaved = true;
            this.imageSaved.emit([...imagesToEmit]);

            // Verifica se é a última imagem para fechar o modal
            const totalImages = this.imagesList().length;
            const isLastImage = totalImages === 1 || currentIndex === totalImages - 1;

            if (isLastImage) {
                this.closeModal();
            }
        } catch (error) {
            this.isSaving.set(false);
        }
    }

    public setTool(tool: string): void {
        if (!this.markerArea) return;
        this._markerOperations.createMarker(this.markerArea, tool);
    }

    public onColorChange(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input && input.value) {
            this.selectedColor.set(input.value);

            // Se há um marcador selecionado, aplica a cor a ele também
            if (this.hasSelectedMarker()) {
                this.applyColorToSelectedMarkers();
            }
        }
    }

    private applyColorToMarkerEditor(markerEditor: any): void {
        if (!markerEditor) return;
        this._markerOperations.applyColorToMarkerEditor(markerEditor, this.selectedColor());
    }

    private applyColorToSelectedMarkers(): void {
        if (!this.markerArea) return;
        this._markerOperations.applyColorToSelectedMarkers(this.markerArea, this.selectedColor());
    }
}
